import React from 'react';
import {
  TextField,
  BooleanField,
  NumberField,
  SimpleShowLayout,
  useRecordContext,
  useResourceContext,
  DateField,
  Labeled,
  TabbedShowLayoutTabs,
  TabbedShowLayout,
  Tab,
  FunctionField,
} from 'react-admin';
import MoneyField from 'components/MoneyField';
import ProRateField from 'components/ProRateField';
import LinkField from 'components/LinkField';
import PercentField from 'components/PercentField';
import TimestampFields from 'components/TimestampFields';

import NullSafeBooleanField from 'components/NullSafeBooleanField';
import { ChargeRateCentsField } from 'components/ChargeRateCentsField';
import RateAudits from 'resources/rate_settings/RateAudits';
import RateMarkupField from 'components/RateMarkupField';
import BlendedRateField from 'components/BlendedRateField';
import { RateSettingsDisplayHelper } from 'utils/displayHelpers/rate-settings';
import { DisplayHelpers } from 'utils/helpers';
import { isNil } from 'lodash';
import {
  Ten99Section,
  W2LocalSection,
  W2TravelSection,
} from '../rate-settings-show/W2And1099SubSettingsShow';

const getLevelPrefix = (resource: string) => {
  if (resource === 'LocationPositionRateSetting') {
    return 'location';
  } else if (resource === 'MarketPositionRateSetting') {
    return 'market';
  } else if (resource === 'StatePositionRateSetting') {
    return 'state';
  } else if (resource === 'MarketLocationTypePositionRateSetting') {
    return 'market_location_type';
  } else if (resource === 'StateLocationTypePositionRateSetting') {
    return 'state_location_type';
  }
  return '';
};

const showPostAndAgencyRatesList = [
  'LocationPositionRateSetting',
  'MarketPositionRateSetting',
  'AgencyLocationPositionRateSetting',
];
const showCanPostList = [...showPostAndAgencyRatesList, 'StatePositionRateSetting'];

const hiddenStyle = { display: 'none' };

const PositionRateSettingsShow = () => {
  const record = useRecordContext();
  const resource = useResourceContext();
  if (!record) {
    return null;
  }
  const {
    user_id,
    market_id,
    state_id,
    settings: inheritedSettings,
    market,
    state,
    location_type_id,
    location_type,
    location_id,
    location,
    agency_id,
    agency,
    position_id,
    position,
    specialty,
  } = record;
  const settingsField =
    resource === 'AgencyLocationPositionRateSetting'
      ? 'settings'
      : `${getLevelPrefix(resource)}_position_rate_setting`;

  const settings = record[settingsField];
  if (!settings) {
    // react-admin optimistic rendering will cause this to be null for a second when navigating from the list view.
    return null;
  }

  const showPostAndAgencyRates = showPostAndAgencyRatesList.includes(resource);
  const showCanPost = showCanPostList.includes(resource);
  const isLocationTypePositionRateSetting =
    resource === 'MarketLocationTypePositionRateSetting' ||
    resource === 'StateLocationTypePositionRateSetting';

  const isLocationLevelPositionRateSetting =
    resource === 'LocationPositionRateSetting' || resource === 'AgencyLocationPositionRateSetting';
  const isMarketPositionRateSetting = resource === 'MarketPositionRateSetting';
  const show12hRatesFields = !isLocationLevelPositionRateSetting;

  const getActiveSource = () => {
    if (resource === 'MarketLocationTypePositionRateSetting') {
      return 'market_location_type_position_rate_setting_active';
    }
    if (resource === 'StateLocationTypePositionRateSetting') {
      return 'state_location_type_position_rate_setting_active';
    }
    return 'active';
  };

  const taxType =
    record.location?.primary_billing_group?.plan === 'enterprise_w2' ? 'W2 ' : '1099 ';
  const isW2 = taxType === 'W2 ';
  const nestedClassificationOperator =
    record.location?.primary_billing_group?.plan === 'enterprise_w2' ? 'w2.local' : 'ten99';
  const rateSettingsDisplayHelper = new RateSettingsDisplayHelper({
    settings,
    location: { ...record?.location },
  });
  const activeStructureLabel = rateSettingsDisplayHelper.getActiveRateStructureLabel();
  const showActiveStructureLabel = isLocationLevelPositionRateSetting && activeStructureLabel;
  const hasBlendedRateMarkup = record[settingsField]
    ? !isNil(record[settingsField]?.ten99?.blended_rates?.blended_rate_markup)
    : !isNil(record[settings]?.ten99?.blended_rates?.blended_rate_markup);

  const hasLocationTypeMarkup = !isNil(record[settingsField]?.location_type_markup);

  return (
    <SimpleShowLayout>
      <TabbedShowLayout tabs={<TabbedShowLayoutTabs variant="scrollable" />}>
        <Tab label="General">
          <TextField label="ID" source="id" />
          {market_id && <LinkField source="market_id" />}
          {market?.display_name && <TextField label="Market Name" source="market.display_name" />}
          {user_id && <LinkField source="user_id" />}
          {state_id && <LinkField source="state_id" />}
          {state?.name && <TextField label="State" source="state.name" />}
          {location_type_id && (
            <LinkField
              source="location_type_id"
              to={`/LocationType/${location_type_id}/show`}
              label="Location Type ID"
            />
          )}
          {location_type?.label && (
            <TextField label="Location Type Name" source="location_type.label" />
          )}
          {location_id && <LinkField label="Location ID" source="location_id" />}
          {location?.name && <TextField label="Location" source="location.name" />}
          {agency_id && <LinkField label="Agency ID" source="agency_id" />}
          {agency?.name && <TextField label="Agency" source="agency.name" />}
          {position_id && <LinkField label="Position ID" source="position_id" />}
          {position?.display_name && (
            <TextField label="Position Name" source="position.display_name" />
          )}
          <LinkField label="Specialty ID" source="specialty_id" />
          {specialty?.label && <TextField label="Specialty" source="specialty.label" />}
          <NullSafeBooleanField source={getActiveSource()} label="Active" />
          {showCanPost && (
            <>
              <Labeled fullWidth label="Active for per-diem shifts">
                <BooleanField source={`${settingsField}.can_post_job`} />
              </Labeled>
              <Labeled fullWidth label="Active for assignments">
                <BooleanField source={`${settingsField}.can_post_assignment`} />
              </Labeled>
            </>
          )}
          {market && <DateField label="Last Review Date" source="last_rate_review" />}
          {isMarketPositionRateSetting ? (
            <FunctionField
              render={({ with_markup_settings, market_position_rate_setting }) => {
                const isBlended = market_position_rate_setting?.ten99?.use_blended_rate;
                const rateSettingsDisplayHelper = new RateSettingsDisplayHelper({
                  settings: isBlended ? with_markup_settings : market_position_rate_setting,
                });
                const applicableHash = rateSettingsDisplayHelper.getSettingsForClassification(
                  isW2 ? 'w2.local' : 'ten99',
                );

                return DisplayHelpers.money(applicableHash?.charge_rate_cents);
              }}
              label={`Applied ${taxType}Per Diem Location Rate${
                showActiveStructureLabel ? ` - ${activeStructureLabel}` : ''
              }`}
            />
          ) : (
            <ChargeRateCentsField
              isLocationTypePositionRateSetting={isLocationTypePositionRateSetting}
              nestedOperator={isW2 ? 'w2.local' : 'ten99'}
              isLocationPositionRateSetting={isLocationLevelPositionRateSetting}
              label={`Applied ${taxType}Per Diem Location Rate${
                showActiveStructureLabel ? ` - ${activeStructureLabel}` : ''
              }`}
            />
          )}

          {inheritedSettings.ten99?.charge_rate_cents ||
          inheritedSettings.w2?.local?.charge_rate_cents ? (
            <ProRateField
              label={`Applied ${taxType}${activeStructureLabel} Professional Rate`}
              nestedOperator={isW2 ? 'w2.local' : 'ten99'}
            />
          ) : (
            <NumberField label={`Applied ${taxType}${activeStructureLabel} Professional Rate`} />
          )}
          {settings.charge_on_call_hourly_rate_cents ? (
            <MoneyField
              label="Location On Call Rate"
              source={`${settingsField}.charge_on_call_hourly_rate_cents`}
            />
          ) : (
            <NumberField label="Location On Call Rate" />
          )}

          <PercentField
            label="On Call Percentage Fee"
            source={`${settingsField}.on_call_fee_percent`}
            record={record}
          />

          {isLocationTypePositionRateSetting && (
            <PercentField
              label="Location Type Markup"
              source={`${settingsField}.location_type_markup`}
              blankIfNull
            />
          )}
          {showPostAndAgencyRates && (
            <MoneyField
              label="Agency Charge Rate"
              source={`${settingsField}.agency_charge_rate_cents`}
            />
          )}
          {show12hRatesFields && (
            <>
              <Labeled fullWidth label="8hr Charge Rate">
                {isLocationTypePositionRateSetting ? (
                  <ChargeRateCentsField
                    isLocationTypePositionRateSetting={isLocationTypePositionRateSetting}
                    nestedOperator={isW2 ? 'w2.local' : 'ten99'}
                    isLocationPositionRateSetting={isLocationLevelPositionRateSetting}
                  />
                ) : (
                  <MoneyField
                    source={`${settingsField}.${nestedClassificationOperator}.charge_rate_cents`}
                  />
                )}
              </Labeled>
              <Labeled fullWidth label="12hr rate active markup">
                {isLocationTypePositionRateSetting ? (
                  <PercentField source="settings.ten99.blended_rates.blended_rate_markup" />
                ) : (
                  <RateMarkupField
                    hasBlendedRateMarkup={hasBlendedRateMarkup}
                    source={isLocationTypePositionRateSetting ? 'settings' : settingsField}
                  />
                )}
              </Labeled>
              <Labeled fullWidth label="12hr Charge Rate">
                {isMarketPositionRateSetting && hasBlendedRateMarkup ? (
                  <MoneyField
                    source={`with_markup_settings.${nestedClassificationOperator}.charge_rate_cents`}
                  />
                ) : isLocationTypePositionRateSetting ? (
                  <MoneyField
                    source={`blended_settings_charge_rate.ten99_with_location_type_markup`}
                  />
                ) : (
                  <BlendedRateField
                    source={
                      isLocationTypePositionRateSetting && hasLocationTypeMarkup
                        ? 'settings'
                        : settingsField
                    }
                    hasBlendedRateMarkup={hasBlendedRateMarkup}
                    hasLocationTypeMarkup={hasLocationTypeMarkup}
                  />
                )}
              </Labeled>
            </>
          )}

          <TimestampFields />
          <div>
            <RateAudits />
          </div>
        </Tab>
        <Tab
          label="1099"
          style={isW2 && resource === 'LocationPositionRateSetting' ? hiddenStyle : {}}
        >
          <Ten99Section source={settingsField} showTitle={false} />
        </Tab>
        <Tab
          label="W2 Local"
          style={!isW2 && resource === 'LocationPositionRateSetting' ? hiddenStyle : {}}
        >
          <W2LocalSection source={settingsField} showTitle={false} />
        </Tab>
        <Tab label="W2 Travel">
          <W2TravelSection source={settingsField} showTitle={false} />
        </Tab>
      </TabbedShowLayout>
    </SimpleShowLayout>
  );
};

export default PositionRateSettingsShow;
